/**
 * Overlay lane priority for APT interstitial-style surfaces.
 * Higher priority (lower index) blocks or preempts lower lanes.
 */

export const OVERLAY_LANES = [
  'consent',
  'apt_popup',
  'provider_acquisition',
  'premium_upgrade',
  'google_overlay',
] as const;

export type OverlayLane = (typeof OVERLAY_LANES)[number];

export type OverlayClaim = {
  lane: OverlayLane;
  id: string;
};

const LANE_PRIORITY: Readonly<Record<OverlayLane, number>> = {
  consent: 0,
  apt_popup: 1,
  provider_acquisition: 2,
  premium_upgrade: 3,
  google_overlay: 4,
};

export function overlayLanePriority(lane: OverlayLane): number {
  return LANE_PRIORITY[lane];
}

/**
 * Whether `requested` may acquire while `active` holds the overlay.
 * - No active claim → allow
 * - Same lane → reject (one claim per lane)
 * - Higher priority (lower number) request → allow (preempt)
 * - Lower or equal priority → reject
 */
export function canAcquireOverlay(
  active: OverlayClaim | null,
  requested: OverlayClaim,
): boolean {
  if (!active) {
    return true;
  }
  if (active.lane === requested.lane) {
    return active.id === requested.id;
  }
  return overlayLanePriority(requested.lane) < overlayLanePriority(active.lane);
}

/** True when requested should preempt (replace) the active claim. */
export function shouldPreemptOverlay(
  active: OverlayClaim | null,
  requested: OverlayClaim,
): boolean {
  if (!active) {
    return false;
  }
  if (active.lane === requested.lane) {
    return false;
  }
  return overlayLanePriority(requested.lane) < overlayLanePriority(active.lane);
}

export function isOverlayLane(value: string): value is OverlayLane {
  return (OVERLAY_LANES as readonly string[]).includes(value);
}
